// eslint-disable-next-line max-classes-per-file
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, of, Subject, throwError } from 'rxjs';
import { Scope } from '@ess/jg-rule-executor';
import { Router } from '@angular/router';
import { parse as parseCookie } from 'cookie';
import { StorageService } from '../storage/storage.service.interface';
import { StorageSelectors } from '../storage/storage.selectors';
import { parseJWTPayload } from '../../parsers/base64/base64';
import { User } from '@src/app/model/userdata';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { LoginServiceConfig } from '@src/app/configs/login.service.config';

export enum FAILURE_REASON {
  UNKNOWN,
  INVALID_CREDENTIALS,
  INVALID_RESPONSE // also when there is NO response (backend down)
}

export class SessionData {
  constructor(
    private _sessionData?: { user?: { id?: string, mfa?: boolean, scopes?: Set<Scope> } }) {
  }

  public get data(): { user?: { id?: string, mfa?: boolean, scopes?: Set<Scope> } } {
    return { ...this._sessionData };
  }

  public get userId(): string | undefined {
    return this.data && this.data.user ? this.data.user.id : undefined;
  }

  public get mfa(): boolean {
    return this.data && this.data.user ? this.data.user.mfa : false;
  }

  public get scopes(): Set<Scope> {
    return this.data && this.data.user ? this.data.user.scopes : new Set();
  }
}

export const DEV_CREDENTIALS = {
  email: 'admin@example.com',
  password: 'Fiets12!',
  userId: '123456',
};

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  public readonly LOGIN_URL: string;
  public readonly LOGOUT_URL: string;
  public readonly REFRESH_URL: string;
  public readonly MFA_URL: string;
  public readonly cookieName: string;

  private _currentUser$: Subject<User> = new Subject();

  private _sessionData: { user?: { id?: string, mfa?: boolean, scopes?: Set<Scope> } } | undefined = undefined;
  private _jwt: string | undefined = undefined;

  public retailerKey: string = null;

  constructor(
    _config: LoginServiceConfig,
    private _httpClient: HttpClient,
    private _storageService: StorageService,
    private _permissionsService: PermissionsService,
    private _apolloService: ApolloService,
    router: Router
  ) {
    this.REFRESH_URL = _config.refreshAccessTokenUrl;
    this.LOGIN_URL = _config.loginUrl;
    this.LOGOUT_URL = _config.logoutUrl;
    this.MFA_URL = _config.mfaUrl;
    this.cookieName = _config.cookieName;

    // need to do it like this in order to lazy-load 'setCurrentUser()'
    // this is necessary to avoid circular dependencies
    setTimeout(() => {
      // Try to load previously stored session data
      const storedSessionData = this._storageService
        .readFromStorage<{ user: { id: string } }>(StorageSelectors.sessionData);
      const currentPath = router.url.split('?')[0];
      this.setSessionData(storedSessionData, false /* don't write to storage, it's already there */);
      if (this.isLoggedIn && this.isMfaAuthenticated && !currentPath.endsWith('payload')) {
        this.setCurrentUser();
      }
    });
  }

  public getCurrentUser$(): Observable<User> {
    return this._currentUser$;
  }

  public get userId(): string | undefined {
    return new SessionData(this._sessionData).userId;
  }

  public get isLoggedIn(): boolean {
    return !!this.userId;
  }

  /**
   * Check if user has authenticated with MFA
   */
  public get isMfaAuthenticated(): boolean {
    return new SessionData(this._sessionData).mfa || false;
  }

  public get userScopes(): Set<Scope> {
    return new SessionData(this._sessionData).scopes || new Set<Scope>();
  }

  /** Works only in browser (will return true), not on mobile (returns false) */
  public static clearCookieWebOnly(cookieName: string): boolean {
    try {
      document.cookie = `${cookieName}= ; expires = Thu, 01 Jan 1970 00:00:00 GMT`;
      return true;
    } catch (e) {
      // this method doesn't work on mobile
    }
    return false;
  }

  /**
   * @returns
   *  `false` if reading cookie values is not supported (ie. on mobile)
   *  undefined if no cookie was found
   *  string with cookie value if cookie is present
   */
  public static getCookieWebOnly(cookieName: string): string | undefined | false {
    try {
      return parseCookie(document.cookie)[cookieName];
    } catch (e) {
      // this method doesn't work on mobile
    }
    return false;
  }

  public static getFailureReason(error: { status: number }): FAILURE_REASON {
    if (error.status === 0) {
      return FAILURE_REASON.INVALID_RESPONSE;
    }

    if (error.status === 400 || error.status === 401) {
      // && error.error === 'Invalid login credentials') {
      return FAILURE_REASON.INVALID_CREDENTIALS;
    }

    return FAILURE_REASON.UNKNOWN;
  }

  public getJwt(): string | undefined {
    return this._jwt;
  }

  /**
   * Login the given user with password.
   *
   * @returns Observable that emits the string-based userID extracted
   * from the JWT received from the backend after successful login.
   */
  public authenticate$(email: string, password: string): Observable<{ user: { id: string } }> {
    const params = { email, password };
    return this._httpClient.post(this.LOGIN_URL, params, {
      observe: 'response',
      withCredentials: true,
      responseType: 'text',
    }).pipe(
      map((response: HttpResponse<any>) => {
        if (!response.ok) {
          throwError(() => new Error('Incorrect username/password'));
        }
        this._jwt = response.body;
        return LoginService.parseJWT(response.body);
      })
    );
  }

  public refreshAccessToken$(): Observable<boolean> {
    // The current JWT is the refresh request's body
    const body = this.getJwt();

    // Make the request
    return this._httpClient.post(this.REFRESH_URL, body, {
      observe: 'response',
      withCredentials: true,
      responseType: 'text',
    }).pipe(
      map((response: HttpResponse<any>) => {
        if (!response.ok) {
          throwError(() => new Error('Incorrect username/password'));
        }

        // Store the new JWT
        this._jwt = response.body;

        // Store the JWT's payload as session data
        this.setSessionData(LoginService.parseJWT(response.body));

        return true;
      }),
      catchError((response: HttpErrorResponse) => {
        if (response.status === 401) {
          return of(false);
        }
        return throwError(() => response);
      })
    );
  }

  public login$(email: string, password: string): Observable<{ user: { id: string } }> {
    return this.authenticate$(email, password)
      .pipe(tap(sessionData => this.setSessionData(sessionData)));
  }

  public logout$(withServerLogout: boolean = true): Observable<void> {
    this._jwt = undefined;
    this._sessionData = undefined;
    // remove currentUser and their permissions on logout
    this._currentUser$.next(null);
    this._permissionsService.setPermissions([]);

    this._storageService.clearStorage();

    // web logout;
    LoginService.clearCookieWebOnly(this.cookieName);
    // server logout
    if (withServerLogout) {
      return this._serverLogout$();
    } else {
      return of(undefined);
    }
  }

  /**
   * Saves the given `sessionData` both in this LoginService instance and in
   * the StorageService so it can be reloaded after application restart.
   *
   * @param sessionData The data to store
   * @param store When explicitly set to false, will skip saving to local storage
   */
  public setSessionData(sessionData: { user?: { id?: string } }, store?: boolean): void {
    this._sessionData = sessionData;
    if (store !== false) {
      this._storageService.setInStorage(StorageSelectors.sessionData, sessionData);
    }
  }

  /**
   * Attempts MFA request. If successful, the backend will supply a new access and refresh token with MFA access
   */
  public performMfa$(code: string): Observable<boolean> {
    return this._httpClient.post(this.MFA_URL, code, {
      observe: 'response',
      withCredentials: true,
      responseType: 'text',
    }).pipe(
      switchMap((response: HttpResponse<any>) => {
        if (response.ok) {
          this._jwt = response.body || undefined;
          if (!!this._jwt) {
            const sessionData = LoginService.parseJWT(this._jwt);
            this.setSessionData(sessionData);
            this.setCurrentUser();
          }
        } else {
          throwError(() => new Error('Incorrect mfa code'));
        }
        return of(response.ok);
      })
    );
  }

  /**
   * Sets the current user and their permissions
   */
  public setCurrentUser(): void {
    this._apolloService.getCurrentUser$()
      .pipe(take(1))
      .subscribe((u) => {
        this._permissionsService.setPermissions(u.roles);
        this._currentUser$.next(u);
      });
  }

  private static parseJWT(jwt: string): { user: { id: string, mfa: boolean, scopes: Set<Scope> } } {
    const payload = JSON.parse(parseJWTPayload(jwt));
    return {
      user:
        {
          id: payload.data.userId,
          mfa: payload.data.mfa === 'true',
          scopes: payload.data.scopes ? new Set(Array.from((payload.data.scopes as string).split(', ')).map(s => new Scope(s))) : new Set<Scope>(),
        },
    };
  }

  private _serverLogout$(): Observable<void> {
    return this._httpClient.post(this.LOGOUT_URL, {}, {
      observe: 'response',
      withCredentials: true,
    }).pipe(
      map((response: HttpResponse<any>) => {
        if (!response.ok) {
          throwError(() => new Error('Failed to log out'));
        }
      })
    );
  }
}
